import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Bell, User, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import './Navbar.css';

const LANG_FLAGS: Record<string, string> = { pl: '🇵🇱', en: '🇬🇧' };

function LangSwitch() {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const current = user?.preferred_language || 'pl';

  const toggle = async () => {
    const next = current === 'pl' ? 'en' : 'pl';
    setSaving(true);
    try {
      i18n.changeLanguage(next);
      localStorage.setItem('i18n_lang', next);
      await api.patch('/auth/me', { preferred_language: next });
      updateUser({ preferred_language: next });
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <button className="navbar-lang" onClick={toggle} disabled={saving} title="Change language / Zmień język">
      {LANG_FLAGS[current] || '🌐'}
    </button>
  );
}

function useUnreadCount() {
  const [count, setCount] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const fetch = () => {
      api.get('/alerts/unread-count').then((r) => setCount(r.data.count)).catch(() => {});
    };
    fetch();
    const id = setInterval(fetch, 60_000);
    return () => clearInterval(id);
  }, [user]);

  return count;
}

export default function Navbar() {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const unread = useUnreadCount();

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <nav className="navbar">
      <div className="navbar-logo">
        <span className="logo-10">10</span><span className="logo-drive">drive</span>
        <span className="logo-moto">moto</span>
      </div>

      <div className="navbar-links">
        <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
          <MapPin size={20} />
          <span>{t('nav.live')}</span>
        </NavLink>
        <NavLink to="/history" className={({ isActive }) => isActive ? 'active' : ''}>
          <Clock size={20} />
          <span>{t('nav.history')}</span>
        </NavLink>
        <NavLink to="/alerts" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="nav-icon-wrap">
            <Bell size={20} />
            {unread > 0 && <span className="nav-badge">{unread > 99 ? '99+' : unread}</span>}
          </span>
          <span>{t('nav.alerts')}</span>
        </NavLink>
        <NavLink to="/account" className={({ isActive }) => isActive ? 'active' : ''}>
          <User size={20} />
          <span>{t('nav.account')}</span>
        </NavLink>
      </div>

      <LangSwitch />

      <button className="navbar-logout" onClick={handleLogout} title="Wyloguj">
        <LogOut size={18} />
      </button>
    </nav>
  );
}
